/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate contacts before sending blast
 * @param {Array} contacts - Array of contact objects
 * @returns {{isValid: boolean, errors: Array, validContacts: Array, invalidContacts: Array}}
 */
export const validateContacts = (contacts) => {
  const errors = [];
  const validContacts = [];
  const invalidContacts = [];

  if (!Array.isArray(contacts) || contacts.length === 0) {
    return {
      isValid: false,
      errors: [{ type: "generic", message: "No contacts to validate" }],
      validContacts: [],
      invalidContacts: [],
    };
  }

  contacts.forEach((contact, index) => {
    const contactErrors = [];

    // Check for required email field
    if (!contact.email || contact.email.trim() === "") {
      contactErrors.push({
        type: "missing_email",
        row: index + 1,
        message: `Row ${index + 1}: Missing email address`,
      });
    } else if (!isValidEmail(contact.email)) {
      contactErrors.push({
        type: "invalid_email",
        row: index + 1,
        email: contact.email,
        message: `Row ${index + 1}: Invalid email format - ${contact.email}`,
      });
    }

    if (contactErrors.length > 0) {
      errors.push(...contactErrors);
      invalidContacts.push({ ...contact, _errors: contactErrors, _row: index + 1 });
    } else {
      validContacts.push(contact);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    validContacts,
    invalidContacts,
  };
};

/**
 * Find duplicate emails in contacts
 * @param {Array} contacts - Array of contact objects
 * @returns {{hasDuplicates: boolean, duplicates: Array}}
 */
export const findDuplicateEmails = (contacts) => {
  const emailCounts = {};
  const duplicates = [];

  contacts.forEach((contact) => {
    const email = (contact.email || "").toLowerCase().trim();
    if (email) {
      emailCounts[email] = (emailCounts[email] || 0) + 1;
    }
  });

  Object.entries(emailCounts).forEach(([email, count]) => {
    if (count > 1) {
      duplicates.push({ email, count });
    }
  });

  return {
    hasDuplicates: duplicates.length > 0,
    duplicates,
  };
};

/**
 * Remove duplicate contacts by email
 * @param {Array} contacts - Array of contact objects
 * @returns {Array} Deduplicated contacts (keeps first occurrence)
 */
export const removeDuplicatesByEmail = (contacts) => {
  const seen = new Set();
  return contacts.filter((contact) => {
    const email = (contact.email || "").toLowerCase().trim();
    if (email && !seen.has(email)) {
      seen.add(email);
      return true;
    }
    return false;
  });
};

/**
 * Validate email template payload
 * @param {Array} payload - Email payload array
 * @returns {{isValid: boolean, errors: Array}}
 */
export const validateEmailPayload = (payload) => {
  const errors = [];
  const requiredFields = ["from", "to", "subject"];

  requiredFields.forEach((field) => {
    const fieldValue = payload.find((p) => p.field === field)?.value;
    if (!fieldValue || fieldValue.trim() === "") {
      errors.push({
        type: "missing_field",
        field,
        message: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
      });
    }
  });

  // Validate from email format (if it's not a variable)
  const fromValue = payload.find((p) => p.field === "from")?.value;
  if (fromValue && !fromValue.includes("{{") && !isValidEmail(fromValue)) {
    errors.push({
      type: "invalid_from",
      field: "from",
      message: "From address must be a valid email",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
